<template>
  <section id="pos-id-film-circuit-ids" class="pos-id-page">
    <b-card>
      <div class="title d-flex justify-content-between align-items-center flex-wrap">
        <p class="mb-1">pos-id 匹配記錄</p>
        <div class="d-flex flex-wrap mb-1">
          <b-button
            size="sm"
            variant="outline-secondary"
            class="handle-button"
            @click="goManagementCenter"
          >
            回目錄
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="handle-button ml-1"
            @click="$router.push({ name: 'pos-id-special-ids' })"
          >
            多影片匹配白名單
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="handle-button ml-1"
            @click="$router.push({ name: 'pos-id-audits' })"
          >
            審查記錄
          </b-button>
        </div>
      </div>

      <b-alert v-if="readOnly" variant="warning" show class="mb-3">{{ readOnlyHint }}</b-alert>

      <div class="pos-id-filters">
        <b-row>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">pos-id（前綴 / 精確）</label>
            <div class="d-flex">
              <b-form-input
                v-model="filters.pos_id"
                placeholder="例：135（留空 = 不過濾）"
                @keyup.enter="search"
              />
              <b-form-select
                v-model="filters.pos_id_mode"
                :options="posIdModeOptions"
                class="ml-1 flex-shrink-0"
                style="width: 92px"
              />
            </div>
            <div class="pos-id-hint mt-1">清空後查詢會恢復「未過濾」，不會送出空字串。</div>
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">院線（可多選）</label>
            <el-select
              v-model="filters.circuit_id"
              multiple
              filterable
              collapse-tags
              placeholder="請選擇院線"
              style="width: 100%"
            >
              <el-option
                v-for="item in circuitOptions"
                :key="item.value"
                :label="item.text"
                :value="item.value"
              />
            </el-select>
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">film_id</label>
            <b-form-input v-model="filters.film_id" placeholder="例：5012" />
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">狀態</label>
            <b-form-select v-model="filters.status" :options="statusOptions" />
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2">
            <label class="pos-id-filter-label">手工鎖定</label>
            <b-form-select v-model="filters.manual_linked" :options="manualLinkedOptions" />
          </b-col>
          <b-col cols="12" md="6" lg="3" class="mb-2 d-flex align-items-end">
            <b-form-checkbox v-model="filters.onlySpecial">只看已登記白名單</b-form-checkbox>
          </b-col>
        </b-row>
        <div class="pos-id-filters__actions">
          <b-button size="sm" variant="primary" class="mr-1" :disabled="loading" @click="search">
            查詢
          </b-button>
          <b-button size="sm" variant="outline-secondary" @click="resetFilters">重置</b-button>
        </div>
      </div>

      <div class="pos-id-table-bar">
        <span class="pos-id-table-bar__title">匹配記錄</span>
        <span class="pos-id-table-bar__hint">
          點擊帶 <i class="el-icon-sort"></i> 的表頭可切換排序（↑ 升序 / ↓ 降序）
        </span>
      </div>

      <div v-loading="loading">
        <b-table
          bordered
          striped
          hover
          responsive
          show-empty
          empty-text="沒有符合條件的匹配記錄"
          :items="tableData"
          :fields="displayFields"
        >
          <template #head()="data">
            <pos-id-sortable-header
              :label="data.field.label"
              :sort-key="data.field.sortKey"
              :active-sort-key="filters.sort"
              :direct="filters.direct"
              @sort="handleHeaderSort"
            />
          </template>
          <template #head(has_special)>
            <span :title="whiteListHint">白名單</span>
          </template>
          <template #cell(circuit)="row">
            <div class="text-nowrap">
              {{ row.item.circuit_name }}
              <span class="text-muted">（{{ row.item.circuit_id }}）</span>
            </div>
          </template>
          <template #cell(pos_id)="row">
            <div class="text-nowrap">{{ row.item.pos_id }}</div>
          </template>
          <template #cell(film_id)="row">
            <span>{{ row.item.film_id }}</span>
            <b-badge v-if="isUnmatched(row.item)" variant="danger" class="ml-1">未匹配</b-badge>
          </template>
          <template #cell(master_id)="row">
            <span class="text-nowrap">{{ row.item.master_id }}</span>
          </template>
          <template #cell(title_name)="row">
            <div style="min-width: 220px">{{ row.item.title_name }}</div>
          </template>
          <template #cell(manual_linked)="row">
            <b-badge v-if="isLocked(row.item)" variant="warning">已鎖定</b-badge>
            <span v-else class="text-muted">否</span>
          </template>
          <template #cell(has_special)="row">
            <b-badge v-if="row.item.has_special === true" variant="success">白名單</b-badge>
            <span v-else-if="row.item.has_special === false" class="text-muted">否</span>
            <span v-else class="text-muted" :title="whiteListHint">—</span>
          </template>
          <template #cell(updated_at)="row">
            <span class="text-nowrap">{{ row.item.updated_at }}</span>
          </template>
          <template #cell(actions)="row">
            <div class="d-flex flex-nowrap">
              <b-button size="sm" variant="outline-info" @click="openDetail(row.item)">詳情</b-button>
              <b-button
                v-if="!readOnly"
                size="sm"
                variant="outline-primary"
                class="ml-1"
                @click="openEdit(row.item)"
              >
                編輯
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                class="ml-1"
                @click="goAudits(row.item)"
              >
                審查記錄{{ auditCountText(row.item) }}
              </b-button>
            </div>
          </template>
        </b-table>

        <pos-id-pagination
          :page="filters.page"
          :count="filters.count"
          :total="total"
          @page-change="onPageChange"
          @count-change="onCountChange"
        />
      </div>
    </b-card>

    <film-circuit-id-detail-drawer
      :visible.sync="detailVisible"
      :record-id="detailId"
      :read-only="readOnly"
      :reload-token="detailReloadToken"
      @edit="openEdit"
      @read-only="setReadOnly"
      @open-record="openSiblingRecord"
      @missing="onDetailMissing"
    />

    <film-circuit-id-edit-dialog
      :visible.sync="editVisible"
      :record="editRecord"
      :read-only="readOnly"
      @submitted="onSubmitted"
      @read-only="setReadOnly"
      @locate="locateRecord"
    />
  </section>
</template>

<script>
import { Message } from 'element-ui'
import { getFilmCircuitIds } from '@/api/posId'
import { getCircuitList } from '@/api/circuit'
import {
  buildFilmCircuitIdQuery,
  isUnmatchedRecord,
  POS_ID_READ_ONLY_HINT,
  POS_ID_STATUS_OPTIONS,
  resolvePosIdError
} from '@/utils/posId'
import FilmCircuitIdDetailDrawer from './components/FilmCircuitIdDetailDrawer.vue'
import FilmCircuitIdEditDialog from './components/FilmCircuitIdEditDialog.vue'
import PosIdPagination from './components/PosIdPagination.vue'
import PosIdSortableHeader from './components/PosIdSortableHeader.vue'

function hasText(value) {
  return value !== undefined && value !== null && String(value).trim() !== ''
}

function firstText(...values) {
  for (let index = 0; index < values.length; index++) {
    if (hasText(values[index])) {
      return String(values[index])
    }
  }
  return ''
}

/**
 * P1 匹配記錄列表頁（guide §2.1 / §5 P1），並掛載 P2 詳情抽屜與 P3 編輯對話框。
 *
 * 鐵律：
 *  - 查詢參數一律走 buildFilmCircuitIdQuery（清空搜尋框會自動刪掉 key，不會送空值）；
 *  - error.code 才是判據，一律用 resolvePosIdError 轉中文；
 *  - 403 且無 error.code → 唯讀（顯示 POS_ID_READ_ONLY_HINT 並隱藏寫入入口）。
 */
export default {
  name: 'FilmCircuitIdPage',
  components: {
    FilmCircuitIdDetailDrawer,
    FilmCircuitIdEditDialog,
    PosIdPagination,
    PosIdSortableHeader
  },
  data() {
    return {
      loading: false,
      readOnly: false,
      total: 0,
      tableData: [],
      circuitOptions: [],
      detailVisible: false,
      detailId: null,
      detailReloadToken: 0,
      editVisible: false,
      editRecord: null,
      missingRetry: false,
      filters: {
        pos_id: '',
        pos_id_mode: 'prefix',
        circuit_id: [],
        film_id: '',
        status: '',
        manual_linked: '',
        onlySpecial: false,
        sort: 'id',
        direct: 'desc',
        page: 1,
        count: 20
      },
      tableFields: [
        { key: 'circuit', label: '院線', sortKey: 'circuit_id' },
        { key: 'pos_id', label: 'pos-id', sortKey: 'pos_id' },
        { key: 'film_id', label: '影片 id', sortKey: 'film_id' },
        { key: 'master_id', label: 'master id' },
        { key: 'title_name', label: '台帳標題' },
        { key: 'manual_linked', label: '鎖定' },
        { key: 'has_special', label: '白名單' },
        { key: 'updated_at', label: '更新時間', sortKey: 'updated_at' },
        { key: 'actions', label: '操作' }
      ],
      posIdModeOptions: [
        { text: '前綴', value: 'prefix' },
        { text: '精確', value: 'exact' }
      ],
      manualLinkedOptions: [
        { text: '不限', value: '' },
        { text: '是（已手工鎖定）', value: '1' },
        { text: '否（未鎖定）', value: '0' }
      ],
      statusOptions: POS_ID_STATUS_OPTIONS,
      readOnlyHint: POS_ID_READ_ONLY_HINT,
      whiteListHint: '此列表未請求 has_special 時顯示「—」；可用上方「只看已登記白名單」篩選查詢'
    }
  },
  computed: {
    // 當前排序列的表頭以 variant=info 高亮（配合 PosIdSortableHeader 的 ↑ / ↓ 標誌）
    displayFields() {
      return this.tableFields.map((field) => {
        if (field.sortKey && field.sortKey === this.filters.sort) {
          return Object.assign({}, field, { variant: 'info' })
        }
        return field
      })
    }
  },
  created() {
    this.applyRouteQuery()
    this.loadCircuitOptions()
    this.fetchList()
  },
  methods: {
    applyRouteQuery() {
      const query = this.$route.query || {}
      if (hasText(query.pos_id)) {
        this.filters.pos_id = String(query.pos_id)
      }
      if (query.pos_id_mode === 'exact') {
        this.filters.pos_id_mode = 'exact'
      }
      if (hasText(query.circuit_id)) {
        this.filters.circuit_id = String(query.circuit_id)
          .split(',')
          .filter((item) => hasText(item))
      }
      if (hasText(query.film_id)) {
        this.filters.film_id = String(query.film_id)
      }
    },
    buildQueryFilters() {
      const filters = this.filters
      let manualLinked = undefined
      if (filters.manual_linked === '1') {
        manualLinked = 1
      } else if (filters.manual_linked === '0') {
        manualLinked = 0
      }
      return {
        pos_id: filters.pos_id,
        pos_id_mode: filters.pos_id_mode,
        circuit_id: filters.circuit_id,
        film_id: filters.film_id,
        status: filters.status,
        manual_linked: manualLinked,
        has_special: filters.onlySpecial === true ? 1 : undefined,
        with_audit_count: 1,
        page: filters.page,
        count: filters.count,
        sort: filters.sort,
        direct: filters.direct
      }
    },
    fetchList() {
      this.loading = true
      const params = buildFilmCircuitIdQuery(this.buildQueryFilters())
      getFilmCircuitIds(params)
        .then((res) => {
          this.loading = false
          this.missingRetry = false
          this.tableData = res && Array.isArray(res.data) ? res.data : []
          this.total = res && res.total !== undefined ? Number(res.total) : 0
          this.mergeCircuitOptionsFromRows(this.tableData)
        })
        .catch((error) => {
          this.loading = false
          this.tableData = []
          this.total = 0
          const info = resolvePosIdError(error)
          Message.error(info.message)
          if (info.isPermissionDenied) {
            this.setReadOnly()
          }
          if (info.code === 10010 && !this.missingRetry) {
            this.missingRetry = true
            this.fetchList()
          }
        })
    },
    search() {
      this.filters.page = 1
      this.fetchList()
    },
    onPageChange(page) {
      this.filters.page = Number(page) || 1
      this.fetchList()
    },
    onCountChange(count) {
      this.filters.count = Number(count) || 20
      this.filters.page = 1
      this.fetchList()
    },
    // 表頭點擊排序（全站習慣）：點同一欄切換升降，改點另一欄時回到預設「新 → 舊」
    handleHeaderSort(sortKey) {
      if (!sortKey) {
        return
      }
      if (this.filters.sort === sortKey) {
        this.filters.direct = this.filters.direct === 'asc' ? 'desc' : 'asc'
      } else {
        this.filters.sort = sortKey
        this.filters.direct = 'desc'
      }
      this.filters.page = 1
      this.fetchList()
    },
    resetFilters() {
      this.filters.pos_id = ''
      this.filters.pos_id_mode = 'prefix'
      this.filters.circuit_id = []
      this.filters.film_id = ''
      this.filters.status = ''
      this.filters.manual_linked = ''
      this.filters.onlySpecial = false
      this.filters.sort = 'id'
      this.filters.direct = 'desc'
      this.filters.page = 1
      this.filters.count = 20
      this.fetchList()
    },
    setReadOnly() {
      if (!this.readOnly) {
        this.readOnly = true
        this.editVisible = false
        Message.warning(this.readOnlyHint)
      }
    },
    loadCircuitOptions() {
      getCircuitList({ page: 1, count: 200 })
        .then((res) => {
          const payload = res ? res.data : null
          const rows = Array.isArray(payload) ? payload : payload && Array.isArray(payload.data) ? payload.data : []
          this.mergeCircuitOptions(rows)
        })
        .catch(() => {
          // 院線清單屬輔助資料，載入失敗不影響主流程：仍可用列表已載入的院線回填
        })
    },
    mergeCircuitOptions(rows) {
      const map = {}
      this.circuitOptions.forEach((option) => {
        map[option.value] = option.text
      })
      rows.forEach((row) => {
        if (!row) {
          return
        }
        const value = firstText(row.circuit_key, row.circuit_id, row.id)
        if (!value) {
          return
        }
        const label = firstText(row.full_name, row.circuit_name)
        map[value] = label ? `${value} · ${label}` : value
      })
      this.circuitOptions = Object.keys(map)
        .sort()
        .map((value) => ({ value, text: map[value] }))
    },
    mergeCircuitOptionsFromRows(rows) {
      const mapped = (Array.isArray(rows) ? rows : []).map((row) => ({
        circuit_key: row.circuit_id,
        circuit_name: row.circuit_name
      }))
      this.mergeCircuitOptions(mapped)
    },
    isUnmatched(row) {
      return isUnmatchedRecord(row)
    },
    isLocked(row) {
      return Boolean(row) && (row.manual_linked === 1 || row.manual_linked === true)
    },
    auditCountText(row) {
      if (row && typeof row.audit_count === 'number') {
        return `（${row.audit_count}）`
      }
      return ''
    },
    openDetail(row) {
      this.detailId = row.id
      this.detailVisible = true
    },
    openEdit(row) {
      if (this.readOnly) {
        Message.warning(this.readOnlyHint)
        return
      }
      this.editRecord = row
      this.editVisible = true
    },
    openSiblingRecord(id) {
      this.detailId = id
    },
    // 10010（記錄不存在）：提示後刷新列表（guide §1.3 / §5 通用規則 4）
    onDetailMissing() {
      this.detailVisible = false
      Message.info('該筆記錄已不存在，列表已重新整理')
      this.fetchList()
    },
    goAudits(row) {
      this.$router.push({
        name: 'pos-id-audits',
        query: {
          pos_id: row.pos_id,
          pos_id_mode: 'exact',
          circuit_id: row.circuit_id
        }
      })
    },
    locateRecord(payload) {
      this.filters.pos_id = payload && hasText(payload.pos_id) ? String(payload.pos_id) : ''
      this.filters.pos_id_mode = 'exact'
      this.filters.page = 1
      this.editVisible = false
      this.detailVisible = false
      Message.info(`已改用 pos-id「${this.filters.pos_id}」精確查詢`)
      this.fetchList()
    },
    onSubmitted() {
      this.detailReloadToken = this.detailReloadToken + 1
      this.fetchList()
    },
    goManagementCenter() {
      this.$router.push({ name: 'management-center' })
    }
  }
}
</script>
